import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Mail, Lock, User, ArrowRight, Globe2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = isSignup ? '/api/auth/register' : '/api/auth/login';
      const body = isSignup
        ? { email, password, name }
        : { email, password };
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Authentication failed');
      login(data);
      navigate('/explore', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/explore';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#030305', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="grid-bg" style={{ position: 'absolute', inset: 0, opacity: 0.3 }} />
      <div className="animate-fadeIn" style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '420px', padding: '0 20px' }}>

        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8" style={{ textDecoration: 'none' }}>
          <Globe2 size={32} color="#00F0FF" />
          <span style={{ fontFamily: 'Rajdhani', fontSize: '28px', fontWeight: 700, color: '#fff', letterSpacing: '0.1em' }}>ORBITOPS</span>
        </Link>

        {/* Card */}
        <div style={{ background: 'rgba(10,12,18,0.95)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '32px', backdropFilter: 'blur(12px)' }}>
          <h1 style={{ fontFamily: 'Rajdhani', fontSize: '24px', fontWeight: 700, color: '#fff', textAlign: 'center', marginBottom: '4px', letterSpacing: '0.05em' }}>
            {isSignup ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p style={{ fontFamily: 'Outfit', fontSize: '14px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginBottom: '24px' }}>
            {isSignup ? 'Start your orbital journey' : 'Sign in to continue exploring'}
          </p>

          {/* Google Auth */}
          <button
            onClick={handleGoogle}
            data-testid="google-signin-btn"
            className="w-full py-3 rounded-xl flex items-center justify-center gap-3 mb-4"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '14px', color: 'rgba(255,255,255,0.8)', letterSpacing: '0.04em' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.1em' }}>OR</span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit}>
            {isSignup && (
              <div className="mb-3">
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <User size={16} color="rgba(255,255,255,0.3)" />
                  <input
                    type="text" placeholder="Name" value={name}
                    onChange={e => setName(e.target.value)} required
                    data-testid="name-input"
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontFamily: 'Outfit', fontSize: '14px' }}
                  />
                </div>
              </div>
            )}
            <div className="mb-3">
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Mail size={16} color="rgba(255,255,255,0.3)" />
                <input
                  type="email" placeholder="Email" value={email}
                  onChange={e => setEmail(e.target.value)} required
                  data-testid="email-input"
                  style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontFamily: 'Outfit', fontSize: '14px' }}
                />
              </div>
            </div>
            <div className="mb-4">
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Lock size={16} color="rgba(255,255,255,0.3)" />
                <input
                  type="password" placeholder="Password" value={password}
                  onChange={e => setPassword(e.target.value)} required minLength={6}
                  data-testid="password-input"
                  style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontFamily: 'Outfit', fontSize: '14px' }}
                />
              </div>
            </div>

            {error && (
              <p data-testid="auth-error" className="mb-3 text-center" style={{ fontFamily: 'JetBrains Mono', fontSize: '12px', color: '#FF4444' }}>{error}</p>
            )}

            <button
              type="submit" disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full py-3 rounded-xl flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #00F0FF, #00FF94)', border: 'none', cursor: 'pointer', fontFamily: 'Rajdhani', fontWeight: 700, fontSize: '15px', color: '#000', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}
            >
              {loading ? 'Processing...' : (isSignup ? 'Create Account' : 'Sign In')}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>

          <p className="mt-5 text-center" style={{ fontFamily: 'Outfit', fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>
            {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              onClick={() => { setIsSignup(!isSignup); setError(''); }}
              data-testid="toggle-auth-mode"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#00F0FF', fontWeight: 600, fontFamily: 'Outfit', fontSize: '13px' }}
            >
              {isSignup ? 'Sign In' : 'Sign Up'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
